import { getFullProfile, getFanScore, getFanTemperature, exportProfile } from '@/lib/fanProfile';
import { X } from 'lucide-react';
import { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsDashboard({ onClose }) {
  const p = getFullProfile();
  const score = getFanScore();
  const temp = getFanTemperature();
  const maxPlatform = Math.max(p.platformClicks.netflix, p.platformClicks.prime, p.platformClicks.youtube, 1);
  const [mailStats, setMailStats] = useState({ totalSubscribers: 0, mongoSubscribers: 0 });

  useEffect(() => {
    fetch(`${API}/mail/stats?adminKey=at-admin-2026`).then(r => r.json()).then(setMailStats).catch(() => {});
  }, []);

  const tempColors = { Cold: '#4A90D9', Warm: '#D4A055', Hot: '#C44', Superfan: '#C9A84C' };

  return (
    <div data-testid="analytics-dashboard" className="fixed inset-0 z-[200] overflow-y-auto" onClick={onClose}>
      <div className="absolute inset-0 bg-black/95 backdrop-blur-sm" />
      <div className="relative z-10 max-w-2xl mx-auto px-5 py-12 min-h-screen" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-10">
          <h2 className="font-['Space_Mono'] text-sm tracking-wide" style={{ color: 'var(--gold)' }}>FAN INTELLIGENCE</h2>
          <button onClick={onClose} style={{ color: 'var(--ash)' }}><X className="w-5 h-5" /></button>
        </div>

        {/* Score + Temperature */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="p-5 rounded-sm" style={{ background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p className="font-['Space_Mono'] text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--ash)' }}>Fan Score</p>
            <p className="font-['Playfair_Display'] text-4xl font-bold" style={{ color: 'var(--gold)' }}>{score}</p>
          </div>
          <div className="p-5 rounded-sm" style={{ background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p className="font-['Space_Mono'] text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--ash)' }}>Temperature</p>
            <p className="font-['Playfair_Display'] text-2xl font-bold" style={{ color: tempColors[temp] || 'var(--ash)' }}>{temp}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: 'Interactions', value: p.totalInteractions },
            { label: 'Films Viewed', value: p.filmsViewed.length },
            { label: 'Trailers', value: p.trailersPlayed.length },
          ].map(s => (
            <div key={s.label} className="p-4 rounded-sm" style={{ background: 'var(--charcoal)' }}>
              <p className="font-['Space_Mono'] text-[9px] uppercase tracking-wider mb-1" style={{ color: 'var(--ash)' }}>{s.label}</p>
              <p className="font-['Inter'] text-xl font-semibold" style={{ color: 'var(--warm-cream)' }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Platform distribution */}
        <div className="p-5 rounded-sm mb-6" style={{ background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)' }}>
          <p className="font-['Space_Mono'] text-[10px] uppercase tracking-wider mb-4" style={{ color: 'var(--ash)' }}>Platform Clicks</p>
          {Object.entries(p.platformClicks).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 mb-2">
              <span className="font-['Space_Mono'] text-[10px] uppercase w-16" style={{ color: 'var(--silver)' }}>{k}</span>
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(v / maxPlatform) * 100}%`, background: 'var(--gold)' }} />
              </div>
              <span className="font-['Space_Mono'] text-[10px] w-6 text-right" style={{ color: 'var(--warm-cream)' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Profile data */}
        <div className="p-5 rounded-sm mb-6" style={{ background: 'var(--charcoal)' }}>
          <p className="font-['Space_Mono'] text-[10px] uppercase tracking-wider mb-3" style={{ color: 'var(--ash)' }}>Inside Profile</p>
          {Object.entries(p.insideData).filter(([, v]) => v && (Array.isArray(v) ? v.length : true)).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1.5 border-b border-white/[0.03]">
              <span className="font-['Space_Mono'] text-[10px] uppercase" style={{ color: 'var(--ash)' }}>{k}</span>
              <span className="font-['Inter'] text-xs" style={{ color: 'var(--warm-cream)' }}>{Array.isArray(v) ? v.join(', ') : v}</span>
            </div>
          ))}
        </div>

        {/* Mail Stats */}
        <div className="p-5 rounded-sm mb-6" style={{ background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)' }}>
          <p className="font-['Space_Mono'] text-[10px] uppercase tracking-wider mb-3" style={{ color: 'var(--ash)' }}>Mailing System</p>
          <div className="flex gap-6">
            <div>
              <p className="font-['Space_Mono'] text-[9px] uppercase" style={{ color: 'var(--ash)' }}>Brevo</p>
              <p className="font-['Playfair_Display'] text-2xl font-bold" style={{ color: 'var(--gold)' }}>{mailStats.totalSubscribers}</p>
            </div>
            <div>
              <p className="font-['Space_Mono'] text-[9px] uppercase" style={{ color: 'var(--ash)' }}>MongoDB</p>
              <p className="font-['Playfair_Display'] text-2xl font-bold" style={{ color: 'var(--warm-cream)' }}>{mailStats.mongoSubscribers}</p>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="flex gap-3 mb-8 text-[10px]" style={{ color: 'var(--ash)' }}>
          <span className="font-['Space_Mono']">Dwell: {Math.floor(p.dwellTime)}s</span>
          <span className="font-['Space_Mono']">Scroll: {p.scrollDepth}%</span>
          <span className="font-['Space_Mono']">RSVPs: {p.rsvps}</span>
        </div>

        {/* Export */}
        <button data-testid="export-data-btn" onClick={exportProfile} className="font-['Space_Mono'] text-[10px] uppercase tracking-wider px-6 py-2.5 rounded-full transition-all duration-300 hover:bg-[rgba(201,168,76,0.1)]" style={{ color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.25)' }}>
          Export Data
        </button>
      </div>
    </div>
  );
}
